import React from 'react';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useMessageHandler } from '../contexts/MessageContext.js';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { getBackgroundColor, getTextColor } from '../themes';

// Custom styled Toolbar component
const CustomToolbar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: getBackgroundColor(theme, 'appBar'),
  color: getTextColor(theme, 'menuHeader'),
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// AppBarComponent functional component
const AppBarComponent = ({ onMenuClick, menuOpen, translit, onTranslitChange, voiceControl, onVoiceControlChange, onSystemPromptChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { isTranscribing, isProcessing, isPlaybackActive, selectedSystemPrompt, setSelectedSystemPrompt } = useMessageHandler();
  const { updateSettings, settings } = useUserSettings();

  const systemPrompts = settings.systemPrompts || [];
  const translitBool = Boolean(translit);
  const voiceControlBool = Boolean(voiceControl);

  const handleSystemPromptChange = (event) => {
    const newValue = event.target.value;
    setSelectedSystemPrompt(newValue);
    // Pass the selected prompt to the parent component
    if (onSystemPromptChange) {
      onSystemPromptChange(newValue);
    }
  };

  const handleTranslitChange = async (event) => {
    const newValue = event.target.checked;
    await updateSettings({ translit: newValue });
    onTranslitChange(event); // Keep the original handler for UI updates
  };

  const handleVoiceControlChange = async (event) => {
    const newValue = event.target.checked;
    await updateSettings({ voiceControl: newValue });
    onVoiceControlChange(event); // Keep the original handler for UI updates
  };

  return (
    <CustomToolbar>
      {/* Application title - hide on mobile */}
      {!isMobile && (
        <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'left' }}>
          {t('app.title')}
        </Typography>
      )}

      {/* System prompts dropdown */}
      <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedSystemPrompt}
            onChange={handleSystemPromptChange}
            displayEmpty
            disabled={isTranscribing || isProcessing || isPlaybackActive}
            renderValue={(selected) => {
              if (!selected) {
                return <Typography
                  sx={{
                    color: 'text.disabled',
                    fontSize: '0.75rem'
                  }}
                >
                  {t(isMobile ? 'appBar.noSystemPromptMobile' : 'appBar.noSystemPrompt')}
                </Typography>;
              } else {
                return <Typography
                  sx={{
                    fontSize: '0.85rem'
                  }}
                >
                  {selected}
                </Typography>;
              }
            }}
          >
            <MenuItem value="">
              {t(isMobile ? 'appBar.noSystemPromptMobile' : 'appBar.noSystemPrompt')}
            </MenuItem>
            {systemPrompts.map((prompt, index) => (
              <MenuItem key={index} value={prompt.name}>
                {prompt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Transliteration and Voice Control switches */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
        <Tooltip title={t('appBar.translitTooltip')} placement="bottom">
          <FormControlLabel
            control={
              <Switch
                checked={translitBool}
                onChange={handleTranslitChange}
                color="secondary"
                disabled={isTranscribing || isProcessing || isPlaybackActive}
              />
            }
            label={t('appBar.translit')}
          />
        </Tooltip>
        {/* Voice Control switch temporarily hidden
        <Tooltip title={t('appBar.voiceControlTooltip')} placement="bottom">
          <FormControlLabel
            control={
              <Switch
                checked={voiceControlBool}
                onChange={handleVoiceControlChange}
                color="secondary"
                disabled={isTranscribing || isProcessing || isPlaybackActive}
              />
            }
            label={t(isMobile ? 'appBar.voiceControlMobile' : 'appBar.voiceControl')}
          />
        </Tooltip>
        */}
      </div>

      {/* Menu button */}
      {!menuOpen && (
        <IconButton
          data-testid="menu-button"
          edge="end"
          color="inherit"
          onClick={onMenuClick}
          disabled={isTranscribing || isProcessing || isPlaybackActive}
          sx={{ padding: 0, margin: 0 }}
        >
          <MenuIcon fontSize="inherit" />
        </IconButton>
      )}
    </CustomToolbar>
  );
};

export default AppBarComponent;